/**
 * Per-step calendar + reminder actions for week-by-week strategy plans.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { CalendarDaysIcon, BellIcon, CheckIcon } from '@heroicons/react/24/outline';

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const StrategyStepActions = ({
  step,
  planTitle = '',
  visionId = null,
  sourceSurface = 'vision',
  className = '',
}) => {
  const [calendarStatus, setCalendarStatus] = useState('idle');
  const [reminderStatus, setReminderStatus] = useState('idle');

  const title = typeof step === 'string' ? step : step?.title || 'Step';
  const detail = typeof step === 'string' ? '' : step?.detail || '';
  const targetDate = typeof step === 'string' ? null : step?.target_date;

  const advicePayload = {
    verdict: planTitle || title,
    number: detail || (targetDate ? `Due ${targetDate}` : ''),
    act_consequence: 'Keeps your plan on schedule.',
    inaction_consequence: 'Delays your Vision target.',
    next_step_24h: title,
    data_confidence: 'medium',
  };

  const calendarEvent = {
    title,
    description: [planTitle, detail].filter(Boolean).join(' — '),
    due_at: targetDate ? `${targetDate}T09:00:00` : undefined,
    kind: 'strategy_step',
  };

  const handleCalendar = async () => {
    setCalendarStatus('working');
    try {
      const res = await axios.post(
        '/api/advice/calendar/export.ics',
        {
          events: [calendarEvent],
          cal_name: title.slice(0, 60),
        },
        { responseType: 'blob' },
      );
      downloadBlob(res.data, `aura-${(targetDate || 'step').slice(0, 10)}.ics`);
      setCalendarStatus('done');
      setTimeout(() => setCalendarStatus('idle'), 2000);
    } catch (err) {
      console.error('Step calendar export failed:', err);
      setCalendarStatus('error');
      setTimeout(() => setCalendarStatus('idle'), 2000);
    }
  };

  const handleReminder = async () => {
    if (!targetDate) {
      setReminderStatus('error');
      setTimeout(() => setReminderStatus('idle'), 2000);
      return;
    }
    setReminderStatus('working');
    try {
      const saveRes = await axios.post('/api/advice/saved', {
        advice_payload: advicePayload,
        vision_id: visionId,
        source_surface: sourceSurface,
        source_ref: `strategy_step_${step?.order || title}`,
      });
      const savedId = saveRes.data?.saved?.saved_id;
      if (!savedId) throw new Error('Save failed');

      await axios.post(`/api/engagement/saved/${savedId}/reminders`, {
        schedule_type: 'once',
        schedule: { at: `${targetDate}T09:00:00`, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' },
        channels: { in_app: true, calendar: false },
        note: detail || title,
      });
      setReminderStatus('done');
      setTimeout(() => setReminderStatus('idle'), 2000);
    } catch (err) {
      console.error('Step reminder failed:', err);
      setReminderStatus('error');
      setTimeout(() => setReminderStatus('idle'), 2000);
    }
  };

  return (
    <div className={`flex flex-wrap gap-2 mt-2 ${className}`}>
      <button
        type="button"
        onClick={handleCalendar}
        disabled={calendarStatus === 'working'}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-hairline text-label text-secondary hover:border-pine hover:text-pine transition-colors"
        title="Add this week to your calendar (.ics)"
      >
        <CalendarDaysIcon className="w-3.5 h-3.5" />
        {calendarStatus === 'working' ? '…' : calendarStatus === 'done' ? 'Added' : 'Calendar'}
      </button>
      <button
        type="button"
        onClick={handleReminder}
        disabled={reminderStatus === 'working' || !targetDate}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-hairline text-label text-secondary hover:border-pine hover:text-pine transition-colors disabled:opacity-40"
        title={targetDate ? 'Save reminder in My plan' : 'Set a target date to enable reminders'}
      >
        {reminderStatus === 'done' ? (
          <CheckIcon className="w-3.5 h-3.5 text-pine" />
        ) : (
          <BellIcon className="w-3.5 h-3.5" />
        )}
        {reminderStatus === 'working' ? '…' : reminderStatus === 'done' ? 'Set' : 'Remind me'}
      </button>
    </div>
  );
};

export default StrategyStepActions;
